import type { ReactElement } from "react";
import { Briefcase, HelpCircle, LogOut, Settings, Shield, User, Users } from "lucide-react";
import React, { forwardRef, useImperativeHandle } from "react";
import { useAuth } from "../../auth/AuthContext";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../transitions/pageTransitions";

export interface UserProfileDropdownHandle {
  toggle: () => void;
  open: () => void;
  close: () => void;
}

// eslint-disable-next-line max-lines-per-function
export const UserProfileDropdown = forwardRef<UserProfileDropdownHandle>((_props, ref): ReactElement => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [focusedIndex, setFocusedIndex] = React.useState(0);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const buttonRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const { user, signOut } = useAuth();

  // Use transition navigation for menu items
  const { transitionTo } = useTransitionNavigation();

  const handleToggle = React.useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleOpen = React.useCallback(() => {
    setIsOpen(true);
    setFocusedIndex(0);
    // Focus the first menu item after it opens
    setTimeout(() => {
      buttonRefs.current[0]?.focus();
    }, 50);
  }, []);

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  // Expose methods to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      toggle: handleToggle,
      open: handleOpen,
      close: handleClose,
    }),
    [handleToggle, handleOpen, handleClose],
  );

  const handleSignOut = React.useCallback(() => {
    signOut();
    handleClose();
    // ProtectedRoute will handle redirect to /sign-in automatically when auth state clears
  }, [signOut, handleClose]);

  const handleProviderPortal = React.useCallback(() => {
    handleClose();
    transitionTo("/provider", pageTransitions.slideHorizontal);
  }, [handleClose, transitionTo]);

  const handleAdminPortal = React.useCallback(() => {
    handleClose();
    transitionTo("/admin", pageTransitions.slideHorizontal);
  }, [handleClose, transitionTo]);

  // Check if user has provider access
  const hasProviderAccess = user ? user.role === "ADMIN" || user.role === "CONTENT_PROVIDER" : false;
  const isAdmin = user?.role === "ADMIN";

  // Build menu items array dynamically based on user role
  const menuItems = React.useMemo(() => {
    const items: Array<{
      label: string;
      icon: React.ElementType;
      onClick: () => void;
      variant?: "danger";
    }> = [];

    if (isAdmin) {
      items.push({
        label: "Admin Portal",
        icon: Shield,
        onClick: handleAdminPortal,
      });
    }

    if (hasProviderAccess) {
      items.push({
        label: "Provider Portal",
        icon: Briefcase,
        onClick: handleProviderPortal,
      });
    }

    items.push(
      {
        label: "Profiles",
        icon: Users,
        onClick: () => {
          handleClose();
          transitionTo("/user/profiles", pageTransitions.slideHorizontal);
        },
      },
      {
        label: "Account",
        icon: User,
        onClick: () => {
          handleClose();
          transitionTo("/user/dashboard", pageTransitions.slideHorizontal);
        },
      },
      {
        label: "App Settings",
        icon: Settings,
        onClick: () => {
          handleClose();
          transitionTo("/user/settings", pageTransitions.slideHorizontal);
        },
      },
      {
        label: "Help",
        icon: HelpCircle,
        onClick: () => {
          handleClose();
          transitionTo("/help", pageTransitions.slideHorizontal);
        },
      },
      {
        label: "Log Out",
        icon: LogOut,
        onClick: handleSignOut,
        variant: "danger" as const,
      },
    );

    return items;
  }, [isAdmin, hasProviderAccess, handleAdminPortal, handleProviderPortal, handleSignOut]);

  // Handle keyboard navigation
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        const nextIndex = (focusedIndex + 1) % menuItems.length;
        setFocusedIndex(nextIndex);
        buttonRefs.current[nextIndex]?.focus();
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        const prevIndex = (focusedIndex - 1 + menuItems.length) % menuItems.length;
        setFocusedIndex(prevIndex);
        buttonRefs.current[prevIndex]?.focus();
        return;
      }

      if (e.key === "Home") {
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex(0);
        buttonRefs.current[0]?.focus();
        return;
      }

      if (e.key === "End") {
        e.preventDefault();
        e.stopPropagation();
        const lastIndex = menuItems.length - 1;
        setFocusedIndex(lastIndex);
        buttonRefs.current[lastIndex]?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [isOpen, handleClose, focusedIndex, menuItems.length]);

  if (!user) {
    return <></>;
  }

  return (
    <div className="relative">
      {/* Profile Button */}
      <button
        onClick={handleToggle}
        className="flex items-center gap-2 text-sm font-medium hover:text-neutral-300 transition-colors p-2 rounded-full hover:bg-white/10"
        aria-label="User menu"
      >
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
          <User size={18} className="text-white" />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <button
            type="button"
            className="fixed inset-0 z-40 bg-transparent"
            onClick={handleClose}
            onKeyDown={(e) => {
              if (e.key === "Escape") handleClose();
            }}
            aria-label="Close menu"
          />

          {/* Menu */}
          <div
            ref={menuRef}
            role="menu"
            className="absolute right-0 top-full mt-2 w-48 bg-neutral-900/50 backdrop-blur-md rounded-lg shadow-xl border border-neutral-800 z-50 outline-none"
          >
            <div className="py-2">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-neutral-800">
                <div className="text-sm font-medium text-white">User</div>
                <div className="text-xs text-neutral-400">{user.email}</div>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                {menuItems.map((item, index) => {
                  const needsSeparator =
                    (item.label === "Profiles" && (isAdmin || hasProviderAccess)) || item.label === "Log Out";

                  return (
                    <React.Fragment key={item.label}>
                      {needsSeparator && <hr className="my-1 border-neutral-800" />}
                      <button
                        ref={(el) => {
                          buttonRefs.current[index] = el;
                        }}
                        role="menuitem"
                        tabIndex={-1}
                        onClick={() => {
                          item.onClick();
                          if (item.label !== "Log Out") {
                            handleClose();
                          }
                        }}
                        className={`w-full px-4 py-2 text-left text-sm text-neutral-200 hover:bg-neutral-800 hover:text-white focus:bg-neutral-800 focus:text-white transition-colors flex items-center gap-3 outline-none ${
                          item.variant === "danger" ? "hover:text-red-400 focus:text-red-400" : ""
                        }`}
                      >
                        <item.icon size={16} />
                        {item.label}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

UserProfileDropdown.displayName = "UserProfileDropdown";
